
import Navbar from '../visa-travel-tips/components/Navbar';
import Footer from '../visa-travel-tips/components/Footer';

export default function AmericanFoodGuide() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-red-600 to-orange-600 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Ẩm thực Mỹ: Những món ăn phải thử
            </h1>
            <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
              Khám phá hương vị đặc trưng từ East Coast đến West Coast
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-teal-600 transition-colors">Trang chủ</a>
            <span>/</span>
            <a href="/visa-travel-tips" className="hover:text-teal-600 transition-colors">Kinh nghiệm du lịch</a>
            <span>/</span>
            <span className="text-gray-900 font-medium">Ẩm thực Mỹ</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-8 mb-12">
            <p className="text-lg leading-relaxed text-gray-700 mb-0">
              Ẩm thực Mỹ không chỉ có hamburger và hot dog. Đây là một nền ẩm thực đa dạng, 
              phản ánh sự pha trộn văn hóa từ khắp nơi trên thế giới. Từ BBQ miền Nam đến 
              seafood New England, từ Tex-Mex đến Asian fusion, mỗi vùng miền đều có những đặc sản riêng biệt.
            </p>
          </div>

          {/* Must-Try American Classics */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🍔 American Classics Must-Try</h2>

            {/* Hamburger */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/eacca085b52aba89dc1c281d60981668.jpg"
                    alt="Classic American Hamburger"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🍔 Classic American Hamburger</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Biểu tượng của ẩm thực Mỹ - bánh mì kẹp thịt bò nướng với lettuce, tomato, pickle, 
                        onion và special sauce. Originated từ Connecticut năm 1900.
                      </p>
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">🏆 Best places to try:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>In-N-Out (CA):</strong> Animal Style, fresh ingredients</li>
                          <li>• <strong>Shake Shack (NYC):</strong> Premium fast-casual</li>
                          <li>• <strong>Five Guys:</strong> Hand-cut fries, customizable</li>
                          <li>• <strong>White Castle:</strong> Original slider (1921)</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">💰 Price range:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• <strong>Fast food:</strong> $3-8 (McDonald's, Burger King)</li>
                        <li>• <strong>Fast-casual:</strong> $8-15 (Shake Shack, Five Guys)</li>
                        <li>• <strong>Restaurant:</strong> $12-25 (gourmet burgers)</li>
                        <li>• <strong>High-end:</strong> $20-40 (craft burgers)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BBQ Ribs */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Smoky%20BBQ%20ribs%20with%20caramelized%20glaze%20on%20wooden%20cutting%20board%2C%20restaurant%20presentation%2C%20barbecue%20sauce%20dripping%2C%20rustic%20American%20dining%20atmosphere%2C%20warm%20lighting&width=120&height=120&seq=bbq-ribs-1&orientation=squarish"
                    alt="BBQ Ribs"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🔥 BBQ Ribs</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Sườn heo nướng chậm với dry rub spices và tangy BBQ sauce. 
                        Mỗi vùng có style riêng: Kansas City (sweet), Texas (dry rub), Carolina (vinegar-based).
                      </p>
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-900 mb-2">🌟 Regional styles:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Kansas City:</strong> Sweet molasses sauce</li>
                          <li>• <strong>Texas:</strong> Dry rub, beef focus</li>
                          <li>• <strong>Carolina:</strong> Vinegar-pepper sauce</li>
                          <li>• <strong>Memphis:</strong> Dry or wet, pork ribs</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">🍽️ Where to find:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• <strong>BBQ joints:</strong> $15-25/rack</li>
                        <li>• <strong>Chain restaurants:</strong> Applebee's, Chili's</li>
                        <li>• <strong>Food trucks:</strong> Authentic local style</li>
                        <li>• <strong>Festivals:</strong> BBQ competitions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mac and Cheese */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Creamy%20macaroni%20and%20cheese%20with%20golden%20top%20in%20cast%20iron%20pan%2C%20comfort%20food%20photography%2C%20melted%20cheese%20texture%2C%20homestyle%20cooking%2C%20warm%20kitchen%20atmosphere&width=120&height=120&seq=mac-cheese-1&orientation=squarish"
                    alt="Mac and Cheese"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🧀 Mac and Cheese</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Comfort food kinh điển - macaroni pasta trong creamy cheese sauce. 
                        Từ boxed Kraft đến gourmet versions với truffle và lobster.
                      </p>
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-900 mb-2">🎯 Variations:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Classic:</strong> Cheddar cheese sauce</li>
                          <li>• <strong>Lobster Mac:</strong> With chunks of lobster</li>
                          <li>• <strong>Truffle Mac:</strong> Gourmet restaurants</li>
                          <li>• <strong>Baked Mac:</strong> Breadcrumb topping</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">📍 Must-try spots:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• <strong>Homeroom (Oakland):</strong> Mac specialists</li>
                        <li>• <strong>S'MAC (NYC):</strong> 12 cheese varieties</li>
                        <li>• <strong>Beecher's (Seattle):</strong> Handmade cheese</li>
                        <li>• <strong>Southern restaurants:</strong> Traditional style</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Apple Pie */}
            <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Classic%20American%20apple%20pie%20with%20golden%20lattice%20crust%2C%20steam%20rising%2C%20rustic%20kitchen%20setting%2C%20traditional%20dessert%20photography%2C%20homemade%20appearance%2C%20warm%20colors&width=120&height=120&seq=apple-pie-1&orientation=squarish"
                    alt="Apple Pie"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">🥧 Apple Pie</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        "As American as apple pie" - dessert biểu tượng với spiced apples trong flaky pastry crust. 
                        Thường serve với vanilla ice cream (à la mode).
                      </p>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-2">🍎 Apple varieties:</h4>
                        <ul className="text-sm space-y-1">
                          <li>• <strong>Granny Smith:</strong> Tart, holds shape well</li>
                          <li>• <strong>Honeycrisp:</strong> Sweet-tart balance</li>
                          <li>• <strong>Gala:</strong> Mild, sweet flavor</li>
                          <li>• <strong>Mixed varieties:</strong> Complex flavor profile</li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-2">🏪 Where to enjoy:</h4>
                      <ul className="text-sm space-y-1">
                        <li>• <strong>Diners:</strong> Classic American experience</li>
                        <li>• <strong>Bakeries:</strong> Fresh-made daily</li>
                        <li>• <strong>Farm stands:</strong> Seasonal, local apples</li>
                        <li>• <strong>Thanksgiving:</strong> Traditional holiday dessert</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Regional Specialties */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🗺️ Đặc sản theo vùng miền</h2>

            <div className="grid md:grid-cols-2 gap-8">
              {/* East Coast */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-blue-900 mb-6 text-center">🦞 East Coast Favorites</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🦞</span>
                      New England Lobster Roll
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Fresh lobster meat trong buttered, toasted roll. Maine style (mayo) vs Connecticut style (warm butter).
                    </p>
                    <div className="text-xs">
                      <strong>Best at:</strong> Maine, Massachusetts coast • <strong>Price:</strong> $18-35
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🍕</span>
                      New York Pizza
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Thin crust, large slices, fold-and-eat style. NYC water makes crust đặc biệt.
                    </p>
                    <div className="text-xs">
                      <strong>Iconic spots:</strong> Joe's, Prince Street Pizza • <strong>Price:</strong> $1-4/slice
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🥯</span>
                      Everything Bagel
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Bagel với cream cheese, lox, capers, onions. NYC Jewish deli tradition.
                    </p>
                    <div className="text-xs">
                      <strong>Must-try:</strong> H&H Bagels, Ess-a-Bagel • <strong>Price:</strong> $8-15
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🦀</span>
                      Maryland Crab Cakes
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Blue crab meat với minimal filler, pan-fried golden. Chesapeake Bay specialty.
                    </p>
                    <div className="text-xs">
                      <strong>Best in:</strong> Baltimore, Annapolis • <strong>Price:</strong> $15-30
                    </div>
                  </div>
                </div>
              </div>

              {/* West Coast */}
              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-green-900 mb-6 text-center">🌮 West Coast Vibes</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🌮</span>
                      California Fish Tacos
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Grilled fish với cabbage slaw, lime, trong corn tortilla. Fresh và light.
                    </p>
                    <div className="text-xs">
                      <strong>Origin:</strong> San Diego • <strong>Price:</strong> $3-8/taco
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🥑</span>
                      Avocado Toast
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Smashed avocado trên artisan bread với toppings như eggs, tomatoes, microgreens.
                    </p>
                    <div className="text-xs">
                      <strong>Trending in:</strong> LA, SF cafes • <strong>Price:</strong> $8-16
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🦪</span>
                      Pacific Oysters
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Fresh từ Pacific Northwest, creamy và briny. Serve với mignonette sauce.
                    </p>
                    <div className="text-xs">
                      <strong>Best regions:</strong> Washington, Oregon • <strong>Price:</strong> $2-5/oyster
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🍷</span>
                      Wine Country Cuisine
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Farm-to-table dining với local ingredients paired với Napa/Sonoma wines.
                    </p>
                    <div className="text-xs">
                      <strong>Experience:</strong> Napa Valley restaurants • <strong>Price:</strong> $50-150/meal
                    </div>
                  </div>
                </div>
              </div>

              {/* South */}
              <div className="bg-red-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-red-900 mb-6 text-center">🔥 Southern Comfort</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🍗</span>
                      Fried Chicken & Waffles
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Crispy fried chicken với fluffy Belgian waffles, drizzled với maple syrup. Sweet meets savory.
                    </p>
                    <div className="text-xs">
                      <strong>Famous at:</strong> Roscoe's (LA), Amy Ruth's (NYC) • <strong>Price:</strong> $12-20
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🌶️</span>
                      Jambalaya
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Louisiana one-pot dish với rice, sausage, chicken, shrimp. Creole và Cajun versions.
                    </p>
                    <div className="text-xs">
                      <strong>Authentic in:</strong> New Orleans • <strong>Price:</strong> $15-25
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🥞</span>
                      Biscuits & Gravy
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Buttery biscuits topped với sausage gravy. Southern breakfast staple.
                    </p>
                    <div className="text-xs">
                      <strong>Best in:</strong> Southern states • <strong>Price:</strong> $6-12
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🐟</span>
                      Catfish Po'boy
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Fried catfish trong French bread với lettuce, tomatoes, pickles, remoulade sauce.
                    </p>
                    <div className="text-xs">
                      <strong>Origin:</strong> Louisiana • <strong>Price:</strong> $8-15
                    </div>
                  </div>
                </div>
              </div>

              {/* Midwest */}
              <div className="bg-yellow-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-yellow-900 mb-6 text-center">🌽 Midwest Hearty</h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🍕</span>
                      Chicago Deep Dish
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Thick crust pizza với cheese bottom, toppings, chunky tomato sauce on top. Pizza-pie hybrid.
                    </p>
                    <div className="text-xs">
                      <strong>Iconic:</strong> Lou Malnati's, Giordano's • <strong>Price:</strong> $15-30
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🌭</span>
                      Chicago Hot Dog
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      All-beef frank với yellow mustard, chopped onions, tomatoes, pickle, sport peppers. Never ketchup!
                    </p>
                    <div className="text-xs">
                      <strong>Rule:</strong> No ketchup allowed • <strong>Price:</strong> $3-8
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🥩</span>
                      Kansas City Burnt Ends
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Cubed brisket point, smoked until caramelized và tender. "Candy" of BBQ.
                    </p>
                    <div className="text-xs">
                      <strong>Origin:</strong> Kansas City • <strong>Price:</strong> $12-18/serving
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                      <span className="text-lg">🥛</span>
                      Cheese Curds
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Fresh cheese curds, often battered và fried. Wisconsin specialty, "squeaky" texture.
                    </p>
                    <div className="text-xs">
                      <strong>Best in:</strong> Wisconsin • <strong>Price:</strong> $6-10
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Food Culture & Etiquette */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🍽️ Văn hóa ăn uống Mỹ</h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <i className="ri-restaurant-line"></i>
                  Dining Etiquette
                </h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-1"></i>
                    <div>
                      <strong>Tipping:</strong> 18-25% tại restaurants, 15% casual dining, 10-15% food delivery
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-1"></i>
                    <div>
                      <strong>Wait to be seated:</strong> Đợi host/hostess dẫn bàn tại restaurants
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-1"></i>
                    <div>
                      <strong>Doggy bag:</strong> Bình thường ask để mang về thức ăn thừa
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-1"></i>
                    <div>
                      <strong>Water:</strong> Miễn phí tại hầu hết restaurants, refills không giới hạn
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-purple-600 mt-1"></i>
                    <div>
                      <strong>Splitting bill:</strong> "Going Dutch" rất phổ biến
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-lg p-6">
                <h4 className="font-semibold text-orange-900 mb-4 flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  Meal Times & Habits
                </h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-sun-line text-orange-600 mt-1"></i>
                    <div>
                      <strong>Breakfast:</strong> 6-10AM, coffee culture mạnh, grab-and-go phổ biến
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-sun-fill text-orange-600 mt-1"></i>
                    <div>
                      <strong>Lunch:</strong> 11AM-2PM, often quick và light, business lunches
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-moon-line text-orange-600 mt-1"></i>
                    <div>
                      <strong>Dinner:</strong> 5-8PM, main meal, family time hoặc social dining
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-cup-line text-orange-600 mt-1"></i>
                    <div>
                      <strong>Coffee breaks:</strong> Multiple times/day, Starbucks culture
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-restaurant-2-line text-orange-600 mt-1"></i>
                    <div>
                      <strong>Happy hour:</strong> 3-7PM, discounted drinks + appetizers
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-6">
              <h4 className="font-semibold text-blue-800 mb-3">🍺 Drinking Culture</h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <ul className="space-y-2">
                  <li>• <strong>Legal age:</strong> 21+ (strict enforcement)</li>
                  <li>• <strong>ID required:</strong> Always bring passport/ID</li>
                  <li>• <strong>Craft beer:</strong> Huge variety, local breweries</li>
                </ul>
                <ul className="space-y-2">
                  <li>• <strong>Wine culture:</strong> Growing, especially CA wines</li>
                  <li>• <strong>Cocktails:</strong> Creative mixology trending</li>
                  <li>• <strong>BYOB:</strong> Some restaurants allow</li>
                </ul>
                <ul className="space-y-2">
                  <li>• <strong>Public drinking:</strong> Prohibited in most places</li>
                  <li>• <strong>Uber culture:</strong> Don't drink and drive</li>
                  <li>• <strong>Last call:</strong> Varies by state (1-4AM)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Food Experiences */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">🎭 Unique Food Experiences</h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🚚</div>
                <h4 className="font-semibold text-pink-900 mb-3">Food Trucks</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Gourmet food từ trucks di động. Từ tacos đến lobster rolls, innovation và convenience.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Price: $5-15/meal</li>
                  <li>• Find via apps: Roaming Hunger</li>
                  <li>• Popular: LA, Portland, Austin</li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🏪</div>
                <h4 className="font-semibold text-green-900 mb-3">Farmer's Markets</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Fresh, local produce và artisan foods. Weekend tradition, community gathering.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Best: Saturday mornings</li>
                  <li>• Famous: Union Square (NYC)</li>
                  <li>• Samples: Try before buying</li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🍻</div>
                <h4 className="font-semibold text-yellow-900 mb-3">Brewery Tours</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Craft beer revolution. Tours, tastings, và beer education tại local breweries.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Price: $15-30/tour</li>
                  <li>• Include: Tastings + education</li>
                  <li>• Hot spots: San Diego, Portland</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🎪</div>
                <h4 className="font-semibold text-red-900 mb-3">Food Festivals</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Seasonal celebrations của local cuisine. BBQ competitions, wine festivals, ethnic food fairs.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Season: Spring-Fall</li>
                  <li>• Entry: $10-50</li>
                  <li>• Experience: Multiple vendors</li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🍽️</div>
                <h4 className="font-semibold text-purple-900 mb-3">Fine Dining</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Michelin-starred restaurants, celebrity chefs, innovative cuisine. Culinary artistry experience.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Price: $100-500/person</li>
                  <li>• Reservation: Weeks ahead</li>
                  <li>• Dress code: Smart casual+</li>
                </ul>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <div className="text-4xl mb-4">🌮</div>
                <h4 className="font-semibold text-blue-900 mb-3">Ethnic Enclaves</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Chinatowns, Little Italy, Koreatown. Authentic international cuisine within America.
                </p>
                <ul className="text-xs space-y-1">
                  <li>• Authentic: Family recipes</li>
                  <li>• Price: Very reasonable</li>
                  <li>• Culture: Language immersion</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Budget Guide */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">💰 Food Budget Guide</h2>

            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-teal-900 mb-6 text-center">🍽️ Daily Food Budget Breakdown</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-6 text-center border-2 border-green-500">
                  <h4 className="font-bold text-green-800 text-lg mb-4">💰 Budget ($25-40/day)</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Breakfast:</strong> $5-8
                      <div className="text-xs text-gray-600">Coffee shop pastry, diner breakfast</div>
                    </div>
                    <div>
                      <strong>Lunch:</strong> $8-15
                      <div className="text-xs text-gray-600">Fast casual, food trucks, delis</div>
                    </div>
                    <div>
                      <strong>Dinner:</strong> $12-20
                      <div className="text-xs text-gray-600">Chain restaurants, pizza, ethnic</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 text-center border-2 border-blue-500">
                  <h4 className="font-bold text-blue-800 text-lg mb-4">✈️ Mid-Range ($50-80/day)</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Breakfast:</strong> $10-15
                      <div className="text-xs text-gray-600">Sit-down breakfast, brunch spots</div>
                    </div>
                    <div>
                      <strong>Lunch:</strong> $15-25
                      <div className="text-xs text-gray-600">Casual dining, gastropubs</div>
                    </div>
                    <div>
                      <strong>Dinner:</strong> $25-45
                      <div className="text-xs text-gray-600">Nice restaurants, steakhouses</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 text-center border-2 border-purple-500">
                  <h4 className="font-bold text-purple-800 text-lg mb-4">🌟 Fine Dining ($100+/day)</h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <strong>Breakfast:</strong> $20-35
                      <div className="text-xs text-gray-600">Hotel restaurants, upscale cafes</div>
                    </div>
                    <div>
                      <strong>Lunch:</strong> $30-60
                      <div className="text-xs text-gray-600">Michelin restaurants, chef counters</div>
                    </div>
                    <div>
                      <strong>Dinner:</strong> $75-200+
                      <div className="text-xs text-gray-600">Fine dining, tasting menus</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-white rounded-lg p-6">
                <h4 className="font-semibold text-teal-800 mb-4 text-center">💡 Money-Saving Tips</h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Happy hours:</strong> 3-7PM discounted drinks + apps</li>
                    <li>• <strong>Lunch specials:</strong> Same restaurant, lower prices</li>
                    <li>• <strong>Food trucks:</strong> Quality food, affordable prices</li>
                    <li>• <strong>Grocery shopping:</strong> Breakfast + snacks</li>
                    <li>• <strong>Groupon:</strong> Restaurant deals up to 50% off</li>
                  </ul>
                  <ul className="space-y-2 text-sm">
                    <li>• <strong>Hotel breakfast:</strong> Often included</li>
                    <li>• <strong>Yelp check-ins:</strong> Sometimes get discounts</li>
                    <li>• <strong>Restaurant week:</strong> Fixed-price menus</li>
                    <li>• <strong>BYOB restaurants:</strong> Save on drinks</li>
                    <li>• <strong>Split entrees:</strong> American portions are large</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Food Safety */}
          <section className="mb-12">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-6 text-center">🛡️ Food Safety & Dietary Needs</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-semibold mb-4 text-orange-100">Allergies & Dietary:</h3>
                  <ul className="space-y-2 text-sm text-orange-100">
                    <li>• Restaurants accommodate allergies</li>
                    <li>• Vegan/vegetarian options common</li>
                    <li>• Gluten-free menus available</li>
                    <li>• Always inform staff of allergies</li>
                    <li>• EpiPen accepted everywhere</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-orange-100">Food Safety:</h3>
                  <ul className="space-y-2 text-sm text-orange-100">
                    <li>• High food safety standards</li>
                    <li>• Health department inspections</li>
                    <li>• Tap water safe to drink</li>
                    <li>• Ice cubes safe in drinks</li>
                    <li>• Salads và raw foods OK</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-orange-100">Cultural Sensitivity:</h3>
                  <ul className="space-y-2 text-sm text-orange-100">
                    <li>• Halal options in cities</li>
                    <li>• Kosher restaurants available</li>
                    <li>• Asian/Indian vegetarian respected</li>
                    <li>• No judgment on dietary choices</li>
                    <li>• Staff trained on restrictions</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Sẵn sàng khám phá ẩm thực Mỹ?</h3>
            <p className="mb-6 text-red-100">
              Chúng tôi sẽ giúp bạn tìm những nhà hàng và món ăn tốt nhất!
            </p>
            <div className="flex flex-col gap-3 max-w-md mx-auto">
              <button className="bg-white text-red-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
                Tư vấn food tour cá nhân
              </button>
              <button className="bg-red-700 hover:bg-red-800 text-white px-8 py-3 rounded-lg text-lg font-semibold border-2 border-white transition-colors whitespace-nowrap cursor-pointer">
                Tải food guide PDF
              </button>
            </div>
          </div>
        </article>
      </div>

      <Footer />
    </div>
  );
}
